import { apiClientJSON } from "../apiClient";

export interface Fleet {
  id: string;
  name: string;
  city_ids: number[];
  payment_configuration_id: string | null;
  organization_id?: string | null;
}

export interface FleetWithCurrency {
  fleet: Fleet | null;
  currency: string | null;
}

export interface ReservationConfig {
  id: string;
  fleet_id: string;
  max_future_reservation_start_datetime: string;
  disallow_vehicle_from_being_rented_before_start_datetime: string;
  allow_cancel_reservation_before_start_datetime: string;
  deposit_amount_minor: number | null;
  no_show_grace_period: string;
}

export interface ReservationConfigWithDeposit {
  id: string;
  fleet_id: string;
  max_future_reservation_start_datetime: string;
  disallow_vehicle_from_being_rented_before_start_datetime: string;
  allow_cancel_reservation_before_start_datetime: string;
  no_show_grace_period: string;
  deposit: {
    amount_minor: number;
    currency: string;
  } | null;
}

export interface CreateReservationConfigRequest {
  fleet_id: string;
  max_future_reservation_start_datetime: string;
  disallow_vehicle_from_being_rented_before_start_datetime: string;
  allow_cancel_reservation_before_start_datetime: string;
  deposit_amount_minor: number | null;
  no_show_grace_period: string;
}

export interface UpdateReservationConfigRequest {
  configuration_id: string;
  max_future_reservation_start_datetime?: string;
  disallow_vehicle_from_being_rented_before_start_datetime?: string;
  allow_cancel_reservation_before_start_datetime?: string;
  deposit_amount_minor?: number | null;
  no_show_grace_period?: string;
}

export const getFleetByCity = (cityId: number): Promise<Fleet | null> =>
  apiClientJSON
    .get(`/backend/api/v1/admin/cities/${cityId}/fleet`)
    .then((r) => r.data.body.fleet);

export const getFleetWithCurrencyByCity = (
  cityId: number
): Promise<FleetWithCurrency> =>
  apiClientJSON
    .get(`/backend/api/v1/admin/cities/${cityId}/fleet`)
    .then((r) => ({
      fleet: r.data.body.fleet,
      currency: r.data.body.currency,
    }));

export const getReservationConfigByFleetId = (
  fleetId: string
): Promise<ReservationConfigWithDeposit | null> =>
  apiClientJSON
    .get(`/backend/api/v1/rest/fleets/${fleetId}/reservation-configuration`)
    .then((r) => r.data.body.configuration)
    .catch((e) => {
      if (e.response?.status === 404) return null;
      throw e;
    });

export const getReservationConfigByCityId = (
  cityId: number
): Promise<ReservationConfigWithDeposit | null> =>
  apiClientJSON
    .get(`/backend/api/v1/rest/cities/${cityId}/reservation-configuration`)
    .then((r) => r.data.body.configuration)
    .catch((e) => {
      if (e.response?.status === 404) return null;
      throw e;
    });

export const createReservationConfig = (
  config: CreateReservationConfigRequest
): Promise<ReservationConfigWithDeposit> =>
  apiClientJSON
    .post("/backend/api/v1/admin/fleet-reservation-configurations", config)
    .then((r) => r.data.body.configuration);

export const updateReservationConfig = (
  request: UpdateReservationConfigRequest
): Promise<ReservationConfigWithDeposit> => {
  const { configuration_id, ...body } = request;
  return apiClientJSON
    .put(
      `/backend/api/v1/admin/fleet-reservation-configurations/${configuration_id}`,
      body
    )
    .then((r) => r.data.body.configuration);
};

export const deleteReservationConfig = (
  configurationId: string
): Promise<void> =>
  apiClientJSON.delete(
    `/backend/api/v1/admin/fleet-reservation-configurations/${configurationId}`
  );

export const getPaymentConfigByCityId = (
  cityId: number
): Promise<{ currency: string } | null> =>
  apiClientJSON
    .get(`/backend/api/v1/rest/cities/${cityId}/payment-configuration`)
    .then((r) => r.data.body.user_payment_configuration)
    .catch((e) => {
      if (e.response?.status === 404) return null;
      throw e;
    });

export interface ReservationResolution {
  id: string;
  reservation_id: string;
  vehicle_id: string | null;
  legacy_vehicle_id: number | null;
  creation_datetime: string;
}

export interface ReservationDeposit {
  amount_minor: number;
  payment_intent_id: string;
  status?: "authorized" | "captured" | "released";
  created_datetime?: string;
  captured_datetime?: string;
  released_datetime?: string;
}

export interface ReservationWithResolution {
  id: string;
  user_id: string;
  legacy_user_id?: number;
  fleet_id: string;
  start_datetime: string;
  creation_datetime: string;
  status: "ACTIVE" | "COMPLETED" | "CANCELED" | "PAYMENT_FAILED";
  selector: {
    type: "EXACT_VEHICLE" | "VEHICLE_TYPE";
    vehicle_id?: string;
    legacy_vehicle_id?: number;
  };
  deposit?: ReservationDeposit;
  resolution?: ReservationResolution;
}

export interface GetReservationsByFleetParams {
  fleet_id: string;
  status?: string;
  start_datetime_from?: string;
  start_datetime_to?: string;
}

export const getReservationsByFleet = (
  params: GetReservationsByFleetParams
): Promise<ReservationWithResolution[]> =>
  apiClientJSON
    .get(`/backend/api/v1/admin/fleets/${params.fleet_id}/reservations`, {
      params: {
        status: params.status,
        start_datetime_from: params.start_datetime_from,
        start_datetime_to: params.start_datetime_to,
      },
    })
    .then((r) => r.data.body.reservations);

export interface VehicleListItem {
  id: number;
  name: string;
  code: number;
}

export const getVehiclesList = (): Promise<VehicleListItem[]> =>
  apiClientJSON
    .get("/station-service/dashboard/vehicles/list")
    .then((r) => r.data);
